import { AudioDirector } from "./audio";
import {
  BASE_PICKUP_DROP_CHANCE,
  BOSS_HP_BY_STAGE,
  GAME_HEIGHT,
  GAME_WIDTH,
  GUARANTEED_PICKUP_INTERVAL,
  HAZARD_RULES,
  MAX_WEAPON_LEVEL,
  MAX_WING_LEVEL,
  MAX_ACTIVE_PICKUPS,
  PLAYER_MAX_HP,
  PLAYER_REPAIR_AMOUNT,
  STAGES,
  TANK_PICKUP_DROP_BONUS,
  TURRET_PICKUP_DROP_BONUS,
  stageByIndex,
  type HazardKind,
  type StageConfig,
} from "./config";

type GamePhase = "idle" | "playing" | "paused" | "ended";
type EnemyKind = "scout" | "weaver" | "turret" | "charger" | "tank" | "boss";
type PickupKind = "power" | "wing" | "shield" | "repair" | "overdrive" | "bomb";

interface Player {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  radius: number;
  hp: number;
  maxHp: number;
  weaponLevel: number;
  weaponCharge: number;
  wingLevel: number;
  bombs: number;
  shield: boolean;
  overdrive: number;
  invincible: number;
  fireCooldown: number;
  dead: boolean;
}

interface Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  damage: number;
  friendly: boolean;
  dead: boolean;
  life: number;
  color: string;
  pierce: number;
  turnRate: number;
  age: number;
}

interface Enemy {
  kind: EnemyKind;
  x: number;
  y: number;
  originX: number;
  vx: number;
  vy: number;
  radius: number;
  hp: number;
  maxHp: number;
  age: number;
  phase: number;
  variant: number;
  fireTimer: number;
  flash: number;
  dead: boolean;
  score: number;
  entered: boolean;
}

interface Pickup {
  kind: PickupKind;
  x: number;
  y: number;
  vy: number;
  age: number;
  radius: number;
  dead: boolean;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  drag: number;
  line: boolean;
}

interface Ring {
  x: number;
  y: number;
  radius: number;
  speed: number;
  life: number;
  maxLife: number;
  color: string;
  width: number;
}

interface FloatingText {
  x: number;
  y: number;
  text: string;
  color: string;
  life: number;
}

interface Star {
  x: number;
  y: number;
  speed: number;
  size: number;
  alpha: number;
}

interface WeatherParticle {
  x: number;
  y: number;
  speed: number;
  drift: number;
  size: number;
  alpha: number;
  phase: number;
}

interface HazardState {
  cooldown: number;
  warning: number;
  active: number;
  x: number;
  secondaryX: number;
  y: number;
  direction: number;
  tick: number;
  cycle: number;
  introduced: boolean;
}

export interface HudSnapshot {
  stageIndex: number;
  stageName: string;
  hp: number;
  maxHp: number;
  weaponLevel: number;
  weaponCharge: number;
  wingLevel: number;
  bombs: number;
  score: number;
  combo: number;
  multiplier: number;
  progress: number;
}

export interface GameResult {
  stageIndex: number;
  score: number;
  maxCombo: number;
  hpPercent: number;
  rank: "S" | "A" | "B" | "C";
}

export interface GameCallbacks {
  onHud(snapshot: HudSnapshot): void;
  onBoss(show: boolean, name: string, ratio: number): void;
  onToast(title: string, caption: string): void;
  onPlayerHit(): void;
  onBossWarning(): void;
  onComplete(result: GameResult): void;
  onGameOver(result: GameResult): void;
}

const pickupLabels: Record<PickupKind, { glyph: string; color: string; title: string; caption: string }> = {
  power: { glyph: "火", color: "#55d9ff", title: "主炮同步提升", caption: "FIREPOWER CORE" },
  wing: { glyph: "翼", color: "#8a86ff", title: "僚机模组接入", caption: "WING MODULE" },
  shield: { glyph: "盾", color: "#66e8cf", title: "相位护盾展开", caption: "PHASE SHIELD" },
  repair: { glyph: "修", color: "#8de7c0", title: "机体结构修复", caption: "HULL REPAIR" },
  overdrive: { glyph: "雷", color: "#b9f4ff", title: "火力过载启动", caption: "OVERDRIVE / 8 SEC" },
  bomb: { glyph: "核", color: "#74b5ff", title: "霆核装填完成", caption: "THUNDER CORE +1" },
};

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.max(minimum, Math.min(maximum, value));

const distanceSquared = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
};

const chance = (probability: number): boolean => Math.random() < probability;

function compactInPlace<T>(items: T[], keep: (item: T) => boolean): void {
  let writeIndex = 0;
  for (let readIndex = 0; readIndex < items.length; readIndex += 1) {
    const item = items[readIndex];
    if (keep(item)) {
      items[writeIndex] = item;
      writeIndex += 1;
    }
  }
  items.length = writeIndex;
}

const liveBullet = (bullet: Bullet): boolean => !bullet.dead;
const liveEnemy = (enemy: Enemy): boolean => !enemy.dead;
const livePickup = (pickup: Pickup): boolean => !pickup.dead;
const liveParticle = (particle: Particle): boolean => particle.life > 0;
const liveRing = (ring: Ring): boolean => ring.life > 0;
const liveFloatingText = (floatingText: FloatingText): boolean => floatingText.life > 0;
const BOSS_FIRE_INTERVALS = [0.76, 0.82, 0.88, 0.76, 0.92, 0.86, 0.9, 0.72] as const;
const FRONTLINE_Y = 225;
const FRONTLINE_GRACE = 0.72;

export class ThunderGame {
  private readonly context: CanvasRenderingContext2D;
  private readonly callbacks: GameCallbacks;
  private readonly audio: AudioDirector;
  private readonly compactViewport =
    typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches;

  private phase: GamePhase = "idle";
  private stageIndex = 0;
  private stage: StageConfig = STAGES[0];
  private player: Player = this.createPlayer();
  private hazard: HazardState = this.createHazardState();
  private bullets: Bullet[] = [];
  private enemies: Enemy[] = [];
  private pickups: Pickup[] = [];
  private particles: Particle[] = [];
  private rings: Ring[] = [];
  private floatingTexts: FloatingText[] = [];
  private stars: Star[] = [];
  private weather: WeatherParticle[] = [];
  private keys = new Set<string>();

  private animationFrame = 0;
  private previousTime = performance.now();
  private worldTime = 0;
  private stageElapsed = 0;
  private waveCooldown = 0;
  private waveIndex = 0;
  private boss: Enemy | null = null;
  private bossSpawned = false;
  private bossPhaseNotified = false;
  private bossFinalPhaseNotified = false;
  private score = 0;
  private combo = 0;
  private maxCombo = 0;
  private comboTimer = 0;
  private enemyKills = 0;
  private endingCountdown = 0;
  private completed = false;
  private screenShake = 0;
  private lightningFlash = 0;
  private pointerActive = false;
  private hudCooldown = 0;
  private spiralAngle = 0;
  private bossVolley = 0;
  private frontlineExposure = 0;
  private frontlineDamageCooldown = 0;
  private frontlineToastShown = false;
  private backgroundGradient: CanvasGradient | null = null;
  private backgroundHaze: CanvasGradient | null = null;
  private backgroundVignette: CanvasGradient | null = null;

  constructor(canvas: HTMLCanvasElement, audio: AudioDirector, callbacks: GameCallbacks) {
    const context = canvas.getContext("2d", { alpha: false, desynchronized: true });
    if (!context) {
      throw new Error("Canvas 2D is not available in this browser.");
    }
    this.context = context;
    this.callbacks = callbacks;
    this.audio = audio;
    this.refreshBackgroundPaint();
    this.createStars();
    this.createWeather();
    this.loop = this.loop.bind(this);
    this.animationFrame = requestAnimationFrame(this.loop);
  }

  get isPlaying(): boolean {
    return this.phase === "playing";
  }

  get isPaused(): boolean {
    return this.phase === "paused";
  }

  get currentStageIndex(): number {
    return this.stageIndex;
  }

  start(stageIndex: number): void {
    this.stageIndex = clamp(Math.floor(stageIndex), 0, STAGES.length - 1);
    this.stage = stageByIndex(this.stageIndex);
    this.phase = "playing";
    this.player = this.createPlayer();
    this.hazard = this.createHazardState();
    this.bullets = [];
    this.enemies = [];
    this.pickups = [];
    this.particles = [];
    this.rings = [];
    this.floatingTexts = [];
    this.stageElapsed = 0;
    this.waveCooldown = 0.55;
    this.waveIndex = 0;
    this.boss = null;
    this.bossSpawned = false;
    this.bossPhaseNotified = false;
    this.bossFinalPhaseNotified = false;
    this.score = 0;
    this.combo = 0;
    this.maxCombo = 0;
    this.comboTimer = 0;
    this.enemyKills = 0;
    this.endingCountdown = 0;
    this.completed = false;
    this.screenShake = 0;
    this.pointerActive = false;
    this.bossVolley = 0;
    this.frontlineExposure = 0;
    this.frontlineDamageCooldown = 0;
    this.frontlineToastShown = false;
    this.keys.clear();
    this.refreshBackgroundPaint();
    this.createWeather();
    this.callbacks.onBoss(false, this.stage.boss, 1);
    this.callbacks.onToast(this.stage.name, this.stage.subtitle);
    this.emitHud(true);
    this.audio.startMusic(this.stageIndex);
  }

  pause(): void {
    if (this.phase === "playing" && !this.endingCountdown) {
      this.phase = "paused";
    }
  }

  resume(): void {
    if (this.phase === "paused") {
      this.phase = "playing";
      this.previousTime = performance.now();
    }
  }

  returnToIdle(): void {
    this.phase = "idle";
    this.bullets = [];
    this.enemies = [];
    this.pickups = [];
    this.particles = [];
    this.rings = [];
    this.floatingTexts = [];
    this.boss = null;
    this.pointerActive = false;
    this.keys.clear();
    this.audio.stopMusic();
    this.callbacks.onBoss(false, this.stage.boss, 1);
  }

  setPointer(x: number, y: number, active: boolean): void {
    this.pointerActive = active;
    this.player.targetX = clamp(x, 24, GAME_WIDTH - 24);
    this.player.targetY = clamp(y, 88, GAME_HEIGHT - 32);
  }

  setKey(code: string, active: boolean): void {
    if (active) {
      this.keys.add(code);
    } else {
      this.keys.delete(code);
    }
  }

  useBomb(): void {
    if (this.phase !== "playing" || this.player.dead || this.player.bombs <= 0) return;
    this.player.bombs -= 1;
    this.player.invincible = Math.max(this.player.invincible, 1.3);
    this.bullets.forEach((bullet) => {
      if (!bullet.friendly) bullet.dead = true;
    });
    [...this.enemies].forEach((enemy) => {
      enemy.hp -= enemy.kind === "boss" ? 380 : 650;
      enemy.flash = 0.2;
      if (enemy.hp <= 0) this.destroyEnemy(enemy);
    });
    this.rings.push({
      x: this.player.x,
      y: this.player.y,
      radius: 8,
      speed: 860,
      life: 0.72,
      maxLife: 0.72,
      color: "#b9f4ff",
      width: 10,
    });
    this.spawnBurst(this.player.x, this.player.y, "#b9f4ff", 42, 330, true);
    this.screenShake = 18;
    this.lightningFlash = 0.65;
    this.audio.bomb();
    this.callbacks.onToast("霆核释放", "HOSTILE FIRE PURGED");
    this.emitHud(true);
  }

  destroy(): void {
    cancelAnimationFrame(this.animationFrame);
    this.audio.stopMusic();
  }

  debugCompleteStage(): void {
    if (this.phase !== "playing") return;
    if (this.boss) {
      this.boss.hp = 0;
      this.destroyEnemy(this.boss);
    } else {
      this.spawnBoss();
      const spawnedBoss = this.enemies.find((enemy) => enemy.kind === "boss");
      if (spawnedBoss) {
        spawnedBoss.hp = 0;
        this.destroyEnemy(spawnedBoss);
      }
    }
  }

  debugSpawnBoss(): void {
    if (this.phase !== "playing" || this.bossSpawned) return;
    this.player.hp = this.player.maxHp;
    this.player.invincible = 12;
    this.player.weaponLevel = MAX_WEAPON_LEVEL;
    this.player.wingLevel = MAX_WING_LEVEL;
    this.player.overdrive = 12;
    this.stageElapsed = this.stage.duration;
    this.spawnBoss();
    this.emitHud(true);
  }

  private createPlayer(): Player {
    return {
      x: GAME_WIDTH / 2,
      y: GAME_HEIGHT - 118,
      targetX: GAME_WIDTH / 2,
      targetY: GAME_HEIGHT - 118,
      radius: this.compactViewport ? 9.5 : 13,
      hp: PLAYER_MAX_HP,
      maxHp: PLAYER_MAX_HP,
      weaponLevel: 1,
      weaponCharge: 0,
      wingLevel: 0,
      bombs: 2,
      shield: false,
      overdrive: 0,
      invincible: 1.25,
      fireCooldown: 0,
      dead: false,
    };
  }

  private createHazardState(): HazardState {
    return {
      cooldown: 3.1,
      warning: 0,
      active: 0,
      x: GAME_WIDTH / 2,
      secondaryX: GAME_WIDTH / 2,
      y: GAME_HEIGHT * 0.45,
      direction: 1,
      tick: 0,
      cycle: 0,
      introduced: false,
    };
  }

  private createStars(): void {
    this.stars = Array.from({ length: 105 }, () => ({
      x: Math.random() * GAME_WIDTH,
      y: Math.random() * GAME_HEIGHT,
      speed: 24 + Math.random() * 115,
      size: 0.45 + Math.random() * 1.65,
      alpha: 0.18 + Math.random() * 0.7,
    }));
  }

  private createWeather(): void {
    this.weather = Array.from({ length: 62 }, () => ({
      x: Math.random() * GAME_WIDTH,
      y: Math.random() * GAME_HEIGHT,
      speed: 70 + Math.random() * 260,
      drift: -35 + Math.random() * 70,
      size: 0.5 + Math.random() * 2.8,
      alpha: 0.12 + Math.random() * 0.4,
      phase: Math.random() * Math.PI * 2,
    }));
  }

  private refreshBackgroundPaint(): void {
    const theme = this.stage.theme;
    const gradient = this.context.createLinearGradient(0, 0, 0, GAME_HEIGHT);
    gradient.addColorStop(0, theme.skyTop);
    gradient.addColorStop(0.58, theme.skyBottom);
    gradient.addColorStop(1, "#030508");
    this.backgroundGradient = gradient;

    const haze = this.context.createRadialGradient(
      GAME_WIDTH * 0.3,
      GAME_HEIGHT * 0.26,
      0,
      GAME_WIDTH * 0.36,
      GAME_HEIGHT * 0.29,
      310,
    );
    haze.addColorStop(0, `${theme.haze}28`);
    haze.addColorStop(1, `${theme.haze}00`);
    this.backgroundHaze = haze;

    const vignette = this.context.createRadialGradient(
      GAME_WIDTH / 2,
      GAME_HEIGHT * 0.48,
      120,
      GAME_WIDTH / 2,
      GAME_HEIGHT * 0.48,
      430,
    );
    vignette.addColorStop(0, "rgba(0,0,0,0)");
    vignette.addColorStop(1, "rgba(0,0,0,0.62)");
    this.backgroundVignette = vignette;
  }

  private isHazardActive(kind: HazardKind): boolean {
    return this.stage.hazardKind === kind && this.hazard.active > 0;
  }

  private updateStageHazard(delta: number): void {
    if (this.player.dead || this.endingCountdown > 0) return;

    if (this.hazard.warning > 0) {
      this.hazard.warning -= delta;
      if (this.hazard.warning <= 0) this.activateStageHazard();
      return;
    }

    if (this.hazard.active > 0) {
      this.hazard.active -= delta;
      this.applyActiveHazard(delta);
      if (this.hazard.active <= 0) {
        const [minimum, maximum] = HAZARD_RULES[this.stage.hazardKind].cooldown;
        this.hazard.active = 0;
        this.hazard.cooldown = minimum + Math.random() * (maximum - minimum);
      }
      return;
    }

    this.hazard.cooldown -= delta;
    if (this.hazard.cooldown <= 0) this.beginHazardWarning();
  }

  private beginHazardWarning(): void {
    const rule = HAZARD_RULES[this.stage.hazardKind];
    this.hazard.cycle += 1;
    this.hazard.warning = rule.warning;
    this.hazard.tick = 0;
    this.hazard.direction = this.hazard.cycle % 2 === 0 ? -1 : 1;

    if (this.stage.hazardKind === "embers") {
      this.hazard.x = 62 + Math.random() * (GAME_WIDTH - 124);
    } else if (this.stage.hazardKind === "sand") {
      this.hazard.x = 88 + Math.random() * (GAME_WIDTH - 176);
    } else if (this.stage.hazardKind === "void") {
      this.hazard.x = 82 + Math.random() * (GAME_WIDTH - 164);
      this.hazard.y = 245 + Math.random() * 310;
    } else if (this.stage.hazardKind === "lightning") {
      this.hazard.x = 64 + Math.random() * 130;
      this.hazard.secondaryX = GAME_WIDTH - 64 - Math.random() * 130;
    } else if (this.stage.hazardKind === "singularity") {
      this.hazard.x = GAME_WIDTH / 2;
      this.hazard.y = GAME_HEIGHT * 0.46;
    }

    if (!this.hazard.introduced) {
      this.hazard.introduced = true;
      this.callbacks.onToast(rule.title, rule.caption);
    }
  }

  private activateStageHazard(): void {
    const kind = this.stage.hazardKind;
    this.hazard.warning = 0;
    this.hazard.active = HAZARD_RULES[kind].active;
    this.hazard.tick = 0;

    if (kind === "embers") {
      for (let index = -2; index <= 2; index += 1) {
        this.createEnemyBullet(
          this.hazard.x + index * 14,
          -18 - Math.abs(index) * 22,
          Math.PI / 2,
          285 + this.stageIndex * 8,
          11.5,
          0,
        );
      }
      this.lightningFlash = Math.max(this.lightningFlash, 0.2);
    } else if (kind === "sand") {
      const pressure = 1.04 + this.stageIndex * 0.09;
      for (let index = 0; index < 5; index += 1) {
        const spreadX = 42 + index * 99;
        this.spawnEnemy("charger", spreadX, -28 - Math.abs(index - 2) * 34, pressure);
      }
    } else if (kind === "lightning") {
      this.bullets.forEach((bullet) => {
        if (
          !bullet.friendly &&
          (Math.abs(bullet.x - this.hazard.x) < 31 ||
            Math.abs(bullet.x - this.hazard.secondaryX) < 31)
        ) {
          bullet.dead = true;
        }
      });
      this.lightningFlash = 0.62;
      this.screenShake = Math.max(this.screenShake, 7);
      this.audio.warning();
    } else if (kind === "ice") {
      this.rings.push({
        x: this.player.x,
        y: this.player.y,
        radius: 18,
        speed: 185,
        life: 0.62,
        maxLife: 0.62,
        color: this.stage.theme.accent,
        width: 4,
      });
    } else if (kind === "singularity") {
      this.screenShake = Math.max(this.screenShake, 4);
    }
  }

  private applyActiveHazard(delta: number): void {
    const kind = this.stage.hazardKind;

    if (kind === "rain") {
      this.player.x += this.hazard.direction * 82 * delta;
      this.bullets.forEach((bullet) => {
        if (!bullet.friendly) {
          bullet.x += this.hazard.direction * 48 * delta;
        }
      });
    } else if (kind === "embers") {
      if (
        this.hazard.tick <= 0 &&
        this.player.invincible <= 0 &&
        Math.abs(this.player.x - this.hazard.x) < 43
      ) {
        this.hazard.tick = 99;
        this.damagePlayer(18);
      }
    } else if (kind === "aurora") {
      const rotation = this.hazard.direction * 0.6 * delta;
      const cosine = Math.cos(rotation);
      const sine = Math.sin(rotation);
      this.bullets.forEach((bullet) => {
        if (bullet.friendly) return;
        const vx = bullet.vx;
        bullet.vx = vx * cosine - bullet.vy * sine;
        bullet.vy = vx * sine + bullet.vy * cosine;
      });
    } else if (kind === "void" || kind === "singularity") {
      const pullDirection = kind === "void" ? 1 : this.hazard.direction;
      const dx = this.hazard.x - this.player.x;
      const dy = this.hazard.y - this.player.y;
      const distance = Math.max(42, Math.hypot(dx, dy));
      const playerForce = kind === "void" ? 132 : 158;
      this.player.x += (dx / distance) * playerForce * pullDirection * delta;
      this.player.y += (dy / distance) * playerForce * pullDirection * delta;

      this.bullets.forEach((bullet) => {
        if (bullet.friendly) return;
        const bulletX = this.hazard.x - bullet.x;
        const bulletY = this.hazard.y - bullet.y;
        const bulletDistance = Math.max(55, Math.hypot(bulletX, bulletY));
        const force = kind === "void" ? 72 : 94;
        bullet.vx += (bulletX / bulletDistance) * force * pullDirection * delta;
        bullet.vy += (bulletY / bulletDistance) * force * pullDirection * delta;
      });
    } else if (kind === "lightning") {
      if (
        this.hazard.tick <= 0 &&
        this.player.invincible <= 0 &&
        (Math.abs(this.player.x - this.hazard.x) < 32 ||
          Math.abs(this.player.x - this.hazard.secondaryX) < 32)
      ) {
        this.hazard.tick = 99;
        this.damagePlayer(21);
      }
    }

    this.player.x = clamp(this.player.x, 22, GAME_WIDTH - 22);
    this.player.y = clamp(this.player.y, 88, GAME_HEIGHT - 30);
  }

  private loop(timestamp: number): void {
    const delta = Math.min(0.033, Math.max(0, (timestamp - this.previousTime) / 1000));
    this.previousTime = timestamp;
    this.worldTime += delta;
    this.updateAmbient(delta);
    if (this.phase === "playing") {
      this.update(delta);
    } else {
      this.updateParticles(delta * 0.4);
    }
    this.draw();
    this.animationFrame = requestAnimationFrame(this.loop);
  }

  private updateAmbient(delta: number): void {
    const speedMultiplier = this.phase === "playing" ? 1 : 0.32;
    this.stars.forEach((star) => {
      star.y += star.speed * delta * speedMultiplier;
      if (star.y > GAME_HEIGHT + 4) {
        star.y = -4;
        star.x = Math.random() * GAME_WIDTH;
      }
    });
    this.weather.forEach((particle) => {
      particle.y += particle.speed * delta * speedMultiplier;
      particle.x += particle.drift * delta * speedMultiplier;
      if (particle.y > GAME_HEIGHT + 30 || particle.x < -40 || particle.x > GAME_WIDTH + 40) {
        particle.y = -20;
        particle.x = Math.random() * GAME_WIDTH;
      }
    });
    this.lightningFlash = Math.max(0, this.lightningFlash - delta * 2.4);
    if (
      this.phase === "playing" &&
      (this.stage.hazardKind === "lightning" || this.stage.hazardKind === "rain") &&
      chance(delta * (this.stage.hazardKind === "lightning" ? 0.44 : 0.13))
    ) {
      this.lightningFlash = 0.18 + Math.random() * 0.18;
    }
  }

  private update(delta: number): void {
    if (this.endingCountdown > 0) {
      this.endingCountdown -= delta;
      this.updateEntities(delta);
      if (this.endingCountdown <= 0) {
        this.finalizeStage();
      }
      return;
    }

    this.stageElapsed += delta;
    this.waveCooldown -= delta;
    this.comboTimer -= delta;
    this.player.invincible = Math.max(0, this.player.invincible - delta);
    this.player.overdrive = Math.max(0, this.player.overdrive - delta);
    this.player.fireCooldown -= delta;
    this.hudCooldown -= delta;

    if (this.comboTimer <= 0 && this.combo > 0) {
      this.combo = 0;
    }

    this.updatePlayer(delta);
    this.updateFrontlinePressure(delta);

    if (!this.bossSpawned) {
      if (this.stageElapsed >= this.stage.duration) {
        this.spawnBoss();
      } else if (this.waveCooldown <= 0) {
        this.spawnWave();
      }
    }

    this.updateEntities(delta);
    this.updateStageHazard(delta);
    this.handleCollisions();
    this.cleanupEntities();

    if (this.boss && !this.boss.dead) {
      const ratio = clamp(this.boss.hp / this.boss.maxHp, 0, 1);
      const secondPhaseThreshold = this.stageIndex === 7 ? 0.62 : 0.5;
      if (ratio < secondPhaseThreshold && !this.bossPhaseNotified) {
        this.bossPhaseNotified = true;
        this.callbacks.onToast("守关单位进入二阶", "PATTERN DENSITY INCREASED");
        this.lightningFlash = 0.35;
        this.screenShake = 6;
      }
      if (this.stageIndex === 7 && ratio < 0.22 && !this.bossFinalPhaseNotified) {
        this.bossFinalPhaseNotified = true;
        this.callbacks.onToast("零号天枢解除限幅", "FINAL PHASE / NO OUTPUT LIMIT");
        this.lightningFlash = 0.62;
        this.screenShake = 10;
        this.audio.warning();
      }
    }

    if (this.hudCooldown <= 0) {
      this.emitHud();
      this.hudCooldown = 0.1;
    }
  }

  private updatePlayer(delta: number): void {
    if (this.player.dead) return;
    let keyboardX = 0;
    let keyboardY = 0;
    if (this.keys.has("ArrowLeft") || this.keys.has("KeyA")) keyboardX -= 1;
    if (this.keys.has("ArrowRight") || this.keys.has("KeyD")) keyboardX += 1;
    if (this.keys.has("ArrowUp") || this.keys.has("KeyW")) keyboardY -= 1;
    if (this.keys.has("ArrowDown") || this.keys.has("KeyS")) keyboardY += 1;

    if (keyboardX !== 0 || keyboardY !== 0) {
      const length = Math.hypot(keyboardX, keyboardY);
      const speed = 330 * (this.isHazardActive("ice") ? 0.56 : 1);
      this.player.x += (keyboardX / length) * speed * delta;
      this.player.y += (keyboardY / length) * speed * delta;
      this.player.targetX = this.player.x;
      this.player.targetY = this.player.y;
    } else if (
      this.pointerActive ||
      Math.abs(this.player.targetX - this.player.x) > 0.8 ||
      Math.abs(this.player.targetY - this.player.y) > 0.8
    ) {
      const followRate = this.isHazardActive("ice") ? 6.5 : 15;
      const follow = 1 - Math.exp(-delta * followRate);
      this.player.x += (this.player.targetX - this.player.x) * follow;
      this.player.y += (this.player.targetY - this.player.y) * follow;
    }

    this.player.x = clamp(this.player.x, 22, GAME_WIDTH - 22);
    this.player.y = clamp(this.player.y, 88, GAME_HEIGHT - 30);

    if (this.player.fireCooldown <= 0) {
      this.firePlayerWeapon();
      const baseRate = 0.165 - this.player.weaponLevel * 0.006;
      this.player.fireCooldown = this.player.overdrive > 0 ? baseRate * 0.48 : baseRate;
    }
  }

  private updateFrontlinePressure(delta: number): void {
    if (this.player.dead) return;

    if (this.player.y < FRONTLINE_Y) {
      this.frontlineExposure += delta;
      if (!this.frontlineToastShown && this.frontlineExposure > 0.12) {
        this.frontlineToastShown = true;
        this.callbacks.onToast("已进入敌方火控层", "FRONTLINE OVERLOAD / PULL BACK");
      }

      if (this.frontlineExposure >= FRONTLINE_GRACE) {
        this.frontlineDamageCooldown = Math.max(0, this.frontlineDamageCooldown - delta);
        const overload = clamp((this.frontlineExposure - FRONTLINE_GRACE) / 1.25, 0, 1);
        this.player.y += (62 + overload * 58) * delta;

        if (this.frontlineDamageCooldown <= 0 && this.player.invincible <= 0) {
          this.frontlineDamageCooldown = 0.62;
          this.player.targetY = Math.max(this.player.targetY, FRONTLINE_Y + 28);
          this.damagePlayer(15 + this.stageIndex * 0.8);
        }
      }
      return;
    }

    this.frontlineExposure = Math.max(0, this.frontlineExposure - delta * 2.4);
    if (this.frontlineExposure <= 0) this.frontlineDamageCooldown = 0;
  }

  private firePlayerWeapon(): void {
    const level = this.player.weaponLevel;
    const count = Math.min(7, 1 + Math.floor(level / 2) * 2);
    const spread = level >= 6 ? 0.12 : 0.105;
    const damage = (13 + level * 1.65) * (this.player.overdrive > 0 ? 1.18 : 1);
    const center = (count - 1) / 2;

    for (let index = 0; index < count; index += 1) {
      const offset = index - center;
      const angle = -Math.PI / 2 + offset * spread;
      this.createPlayerBullet(
        this.player.x + offset * 4.7,
        this.player.y - 22,
        angle,
        650,
        damage * (Math.abs(offset) > 2 ? 0.72 : 1),
        level >= 7 && Math.abs(offset) < 0.5 ? 2 : 1,
      );
    }

    if (level === 1) {
      this.createPlayerBullet(this.player.x - 6, this.player.y - 18, -Math.PI / 2, 650, damage, 1);
      this.createPlayerBullet(this.player.x + 6, this.player.y - 18, -Math.PI / 2, 650, damage, 1);
    }

    for (let wing = 0; wing < this.player.wingLevel; wing += 1) {
      const wingOffset = 24 + wing * 7;
      const outward = 0.025 + wing * 0.035;
      this.createPlayerBullet(
        this.player.x - wingOffset,
        this.player.y - 7,
        -Math.PI / 2 - outward,
        595,
        damage * 0.6,
        1,
      );
      this.createPlayerBullet(
        this.player.x + wingOffset,
        this.player.y - 7,
        -Math.PI / 2 + outward,
        595,
        damage * 0.6,
        1,
      );
    }

    this.audio.shoot(level);
  }

  private createPlayerBullet(
    x: number,
    y: number,
    angle: number,
    speed: number,
    damage: number,
    pierce: number,
  ): void {
    this.bullets.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      radius: pierce > 1 ? 4.2 : 3,
      damage,
      friendly: true,
      dead: false,
      life: 1.45,
      color: this.player.overdrive > 0 ? "#b9f4ff" : this.stage.theme.accent,
      pierce,
      turnRate: 0,
      age: 0,
    });
  }

  private spawnWave(): void {
    const pressure = 1 + this.stageIndex * 0.09;
    const pattern = this.waveIndex % 6;
    const baseDelay = clamp(1.5 - this.stageIndex * 0.07, 0.92, 1.5);
    this.waveCooldown = baseDelay + Math.random() * 0.55;
    this.waveIndex += 1;

    if (this.waveIndex % 4 === 0) {
      this.spawnSignatureWave(pressure);
      return;
    }

    if (pattern === 0) {
      const center = 90 + Math.random() * 300;
      for (let index = -2; index <= 2; index += 1) {
        this.spawnEnemy("scout", clamp(center + index * 46, 35, 445), -25 - Math.abs(index) * 18, pressure);
      }
      return;
    }

    if (pattern === 1) {
      const leftToRight = chance(0.5);
      for (let index = 0; index < 4; index += 1) {
        const x = leftToRight ? 42 + index * 44 : GAME_WIDTH - 42 - index * 44;
        this.spawnEnemy("weaver", x, -25 - index * 62, pressure);
      }
      return;
    }

    if (pattern === 2) {
      this.spawnEnemy("turret", 105, -34, pressure);
      this.spawnEnemy("turret", 375, -74, pressure);
      if (this.stageIndex >= 2) this.spawnEnemy("scout", 240, -125, pressure);
      return;
    }

    if (pattern === 3) {
      for (let index = 0; index < 3 + Math.min(2, Math.floor(this.stageIndex / 3)); index += 1) {
        this.spawnEnemy("charger", 65 + Math.random() * 350, -30 - index * 70, pressure);
      }
      return;
    }

    if (pattern === 4) {
      this.spawnEnemy("tank", 80 + Math.random() * 320, -45, pressure);
      if (this.stageIndex >= 3) {
        this.spawnEnemy("weaver", 52, -105, pressure);
        this.spawnEnemy("weaver", 428, -145, pressure);
      }
      return;
    }

    const count = 4 + Math.min(3, Math.floor(this.stageIndex / 2));
    for (let index = 0; index < count; index += 1) {
      const x = 42 + (index / Math.max(1, count - 1)) * (GAME_WIDTH - 84);
      this.spawnEnemy(index % 2 === 0 ? "scout" : "weaver", x, -30 - Math.sin(index) * 30, pressure);
    }
  }

  private spawnSignatureWave(pressure: number): void {
    if (this.stageIndex === 0) {
      for (let index = 0; index < 6; index += 1) {
        this.spawnEnemy("scout", 38 + index * 81, -28 - index * 24, pressure);
      }
    } else if (this.stageIndex === 1) {
      for (let index = 0; index < 4; index += 1) {
        this.spawnEnemy("charger", 72 + index * 112, -36 - (index % 2) * 76, pressure);
      }
    } else if (this.stageIndex === 2) {
      this.spawnEnemy("tank", GAME_WIDTH / 2, -48, pressure);
      this.spawnEnemy("charger", 74, -92, pressure);
      this.spawnEnemy("charger", GAME_WIDTH - 74, -128, pressure);
    } else if (this.stageIndex === 3) {
      for (let index = 0; index < 5; index += 1) {
        this.spawnEnemy("weaver", 45 + index * 97, -32 - Math.abs(index - 2) * 42, pressure);
      }
    } else if (this.stageIndex === 4) {
      this.spawnEnemy("turret", 98, -42, pressure);
      this.spawnEnemy("tank", GAME_WIDTH / 2, -96, pressure);
      this.spawnEnemy("turret", GAME_WIDTH - 98, -142, pressure);
    } else if (this.stageIndex === 5) {
      this.spawnEnemy("tank", GAME_WIDTH / 2, -48, pressure);
      this.spawnEnemy("weaver", 72, -108, pressure);
      this.spawnEnemy("weaver", GAME_WIDTH - 72, -154, pressure);
    } else if (this.stageIndex === 6) {
      this.spawnEnemy("turret", 86, -44, pressure);
      this.spawnEnemy("turret", GAME_WIDTH - 86, -82, pressure);
      for (let index = 0; index < 3; index += 1) {
        this.spawnEnemy("charger", 150 + index * 90, -132 - index * 42, pressure);
      }
    } else {
      this.spawnEnemy("tank", GAME_WIDTH / 2, -48, pressure);
      this.spawnEnemy("weaver", 62, -105, pressure);
      this.spawnEnemy("weaver", GAME_WIDTH - 62, -138, pressure);
      this.spawnEnemy("charger", 152, -178, pressure);
      this.spawnEnemy("charger", GAME_WIDTH - 152, -210, pressure);
    }
  }

  private spawnEnemy(kind: EnemyKind, x: number, y: number, pressure: number): Enemy {
    const stats: Record<Exclude<EnemyKind, "boss">, { radius: number; hp: number; speed: number; score: number }> = {
      scout: { radius: 13, hp: 34, speed: 118, score: 120 },
      weaver: { radius: 15, hp: 54, speed: 92, score: 180 },
      turret: { radius: 19, hp: 105, speed: 60, score: 280 },
      charger: { radius: 15, hp: 72, speed: 70, score: 240 },
      tank: { radius: 25, hp: 230, speed: 43, score: 520 },
    };
    const base = kind === "boss" ? { radius: 72, hp: 1600, speed: 20, score: 10000 } : stats[kind];
    const hp = base.hp * pressure;
    const enemy: Enemy = {
      kind,
      x,
      y,
      originX: x,
      vx: 0,
      vy: base.speed * (0.92 + Math.random() * 0.18),
      radius: base.radius,
      hp,
      maxHp: hp,
      age: 0,
      phase: Math.random() * Math.PI * 2,
      variant: Math.floor(Math.random() * 4),
      fireTimer: 0.35 + Math.random() * 1.1,
      flash: 0,
      dead: false,
      score: base.score,
      entered: false,
    };
    this.enemies.push(enemy);
    return enemy;
  }

  private spawnBoss(): void {
    if (this.bossSpawned) return;
    this.bossSpawned = true;
    this.enemies.forEach((enemy) => {
      if (enemy.kind !== "boss") {
        enemy.vy += 250;
      }
    });
    this.bullets.forEach((bullet) => {
      if (!bullet.friendly) bullet.dead = true;
    });
    const hp = BOSS_HP_BY_STAGE[this.stageIndex];
    const boss: Enemy = {
      kind: "boss",
      x: GAME_WIDTH / 2,
      y: -110,
      originX: GAME_WIDTH / 2,
      vx: 0,
      vy: 0,
      radius: 70,
      hp,
      maxHp: hp,
      age: 0,
      phase: 0,
      variant: this.stageIndex,
      fireTimer: 2.4,
      flash: 0,
      dead: false,
      score: 12000 + this.stageIndex * 4000,
      entered: false,
    };
    this.enemies.push(boss);
    this.boss = boss;
    this.bossVolley = 0;
    this.bossFinalPhaseNotified = false;
    this.callbacks.onBossWarning();
    this.callbacks.onToast("高能单位接近", this.stage.bossTitle);
    this.audio.warning();
    window.setTimeout(() => this.audio.intensifyBoss(), 1200);
    this.lightningFlash = 0.5;
  }

  private updateEntities(delta: number): void {
    this.bullets.forEach((bullet) => {
      bullet.age += delta;
      bullet.life -= delta;
      if (bullet.turnRate !== 0) {
        const speed = Math.hypot(bullet.vx, bullet.vy);
        const angle = Math.atan2(bullet.vy, bullet.vx) + bullet.turnRate * delta;
        bullet.vx = Math.cos(angle) * speed;
        bullet.vy = Math.sin(angle) * speed;
      }
      bullet.x += bullet.vx * delta;
      bullet.y += bullet.vy * delta;
      if (
        bullet.life <= 0 ||
        bullet.x < -50 ||
        bullet.x > GAME_WIDTH + 50 ||
        bullet.y < -70 ||
        bullet.y > GAME_HEIGHT + 70
      ) {
        bullet.dead = true;
      }
    });

    this.enemies.forEach((enemy) => this.updateEnemy(enemy, delta));

    this.pickups.forEach((pickup) => {
      pickup.age += delta;
      pickup.y += pickup.vy * delta;
      pickup.x += Math.sin(pickup.age * 3.2) * 24 * delta;
      if (pickup.y > GAME_HEIGHT + 35) pickup.dead = true;
    });

    this.updateParticles(delta);
    this.screenShake = Math.max(0, this.screenShake - delta * 22);
  }

  private updateEnemy(enemy: Enemy, delta: number): void {
    if (enemy.dead) return;
    enemy.age += delta;
    enemy.fireTimer -= delta;
    enemy.flash = Math.max(0, enemy.flash - delta);

    if (enemy.kind === "boss") {
      this.updateBoss(enemy, delta);
      return;
    }

    if (enemy.kind === "scout") {
      enemy.y += enemy.vy * delta;
      enemy.x = enemy.originX + Math.sin(enemy.age * 2.8 + enemy.phase) * 34;
      if (enemy.fireTimer <= 0 && enemy.y > 70 && enemy.y < 440 && chance(0.7)) {
        this.fireAimed(enemy.x, enemy.y + 9, 215 + this.stageIndex * 7, 9 + this.stageIndex * 0.8);
        enemy.fireTimer = 1.8 + Math.random() * 0.7;
      }
    } else if (enemy.kind === "weaver") {
      enemy.y += enemy.vy * delta;
      enemy.x = enemy.originX + Math.sin(enemy.age * 3.8 + enemy.phase) * 82;
      if (enemy.fireTimer <= 0 && enemy.y > 55 && enemy.y < 470) {
        this.fireFan(enemy.x, enemy.y + 10, Math.PI / 2, 3, 0.19, 190 + this.stageIndex * 8, 9);
        enemy.fireTimer = 1.55 + Math.random() * 0.45;
      }
    } else if (enemy.kind === "turret") {
      if (enemy.y < 132) {
        enemy.y += enemy.vy * delta;
      } else {
        enemy.entered = true;
        enemy.x = enemy.originX + Math.sin(enemy.age * 0.92 + enemy.phase) * 66;
        enemy.y += 14 * delta;
      }
      if (enemy.fireTimer <= 0 && enemy.entered) {
        this.fireAimedBurst(enemy.x, enemy.y + 17, 3, 0.09, 230 + this.stageIndex * 8, 11);
        enemy.fireTimer = 1.25 + Math.random() * 0.45;
      }
    } else if (enemy.kind === "charger") {
      if (enemy.age < 1.2) {
        enemy.y += enemy.vy * delta;
        enemy.x += Math.sin(enemy.age * 6 + enemy.phase) * 20 * delta;
      } else {
        if (!enemy.entered) {
          enemy.entered = true;
          const angle = Math.atan2(this.player.y - enemy.y, this.player.x - enemy.x);
          enemy.vx = Math.cos(angle) * 210;
          enemy.vy = Math.sin(angle) * 210;
        }
        enemy.x += enemy.vx * delta;
        enemy.y += enemy.vy * delta;
      }
    } else {
      enemy.y += enemy.vy * delta;
      enemy.x = enemy.originX + Math.sin(enemy.age * 1.15 + enemy.phase) * 46;
      if (enemy.fireTimer <= 0 && enemy.y > 60 && enemy.y < 390) {
        this.fireRadial(enemy.x, enemy.y + 12, 8 + Math.floor(this.stageIndex / 2), 145 + this.stageIndex * 6, 13);
        enemy.fireTimer = 1.75 + Math.random() * 0.4;
      }
    }

    if (enemy.y > GAME_HEIGHT + 90 || enemy.x < -120 || enemy.x > GAME_WIDTH + 120) {
      enemy.dead = true;
    }
  }

  private updateBoss(boss: Enemy, delta: number): void {
    if (boss.age < 2.2) {
      const t = Math.min(1, boss.age / 2.2);
      const ease = 1 - Math.pow(1 - t, 3);
      boss.y = -100 + ease * 238;
      return;
    }

    boss.entered = true;
    const healthRatio = boss.hp / boss.maxHp;
    const rage = healthRatio < (this.stageIndex === 7 ? 0.62 : 0.5);
    const desperation = this.stageIndex === 7 && healthRatio < 0.22;
    boss.x =
      GAME_WIDTH / 2 +
      Math.sin(boss.age * (desperation ? 1.04 : rage ? 0.78 : 0.57)) *
        (desperation ? 170 : rage ? 152 : 128);
    boss.y = 136 + Math.sin(boss.age * 1.13) * 18;

    if (boss.fireTimer <= 0) {
      this.fireBossPattern(boss, rage);
      boss.fireTimer =
        BOSS_FIRE_INTERVALS[this.stageIndex] *
        (rage ? 0.7 : 1) *
        (desperation ? 0.68 : 1);
    }

    const reinforcementInterval = this.stageIndex === 7 ? 5 : 6;
    if (
      this.stageIndex >= 2 &&
      Math.floor(boss.age) % reinforcementInterval === 0 &&
      boss.age % 1 < delta &&
      chance(this.stageIndex === 7 ? 1 : 0.65)
    ) {
      const side = chance(0.5) ? 1 : -1;
      this.spawnEnemy(
        this.stageIndex === 7 ? "weaver" : "scout",
        side > 0 ? 35 : GAME_WIDTH - 35,
        boss.y + 25,
        1 + this.stageIndex * 0.08,
      );
    }
  }

  private fireBossPattern(boss: Enemy, rage: boolean): void {
    this.bossVolley += 1;
    const volley = this.bossVolley;
    const speed = 178 + this.stageIndex * 9 + (rage ? 28 : 0);
    const damage = 12 + this.stageIndex * 0.85;

    if (this.stageIndex === 0) {
      const aim = Math.atan2(this.player.y - boss.y, this.player.x - boss.x);
      this.fireFan(boss.x, boss.y + 42, aim, rage ? 7 : 5, 0.14, speed, damage);
      if (volley % 3 === 0) {
        const offset = volley % 2 === 0 ? 28 : 0;
        for (let x = 34 + offset; x < GAME_WIDTH; x += 92) {
          this.createEnemyBullet(x, -12, Math.PI / 2, speed * 1.08, damage, 0);
        }
      }
    } else if (this.stageIndex === 1) {
      const count = rage ? 16 : 12;
      this.spiralAngle += rage ? 0.28 : 0.2;
      this.fireRadial(boss.x, boss.y + 12, count, speed * 0.83, damage, this.spiralAngle);
      if (volley % 2 === 0) {
        this.fireAimedBurst(boss.x, boss.y + 34, rage ? 5 : 3, 0.075, speed * 1.28, damage);
      }
    } else if (this.stageIndex === 2) {
      const leftX = boss.x - 52;
      const rightX = boss.x + 52;
      const leftAim = Math.atan2(this.player.y - boss.y, this.player.x - leftX);
      const rightAim = Math.atan2(this.player.y - boss.y, this.player.x - rightX);
      this.fireFan(leftX, boss.y + 25, leftAim + 0.11, rage ? 4 : 3, 0.12, speed, damage);
      this.fireFan(rightX, boss.y + 25, rightAim - 0.11, rage ? 4 : 3, 0.12, speed, damage);
      if (volley % 4 === 0 && this.enemies.length < 7) {
        this.spawnEnemy("charger", 48, boss.y + 14, 1.2);
        this.spawnEnemy("charger", GAME_WIDTH - 48, boss.y + 14, 1.2);
      }
    } else if (this.stageIndex === 3) {
      this.spiralAngle += rage ? 0.48 : 0.31;
      const streams = rage ? 5 : 4;
      const center = (streams - 1) / 2;
      const turn = (volley % 2 === 0 ? -1 : 1) * (rage ? 0.38 : 0.25);
      for (let index = 0; index < streams; index += 1) {
        const angle = Math.PI / 2 + (index - center) * 0.32 + Math.sin(this.spiralAngle) * 0.08;
        this.createEnemyBullet(boss.x, boss.y + 20, angle, speed * 0.92, damage, turn);
      }
      if (volley % 3 === 0) {
        this.fireAimedBurst(boss.x, boss.y + 38, rage ? 5 : 3, 0.1, speed * 1.28, damage);
      }
    } else if (this.stageIndex === 4) {
      const safeCenter = 72 + ((volley * 97) % (GAME_WIDTH - 144));
      for (let x = 26; x < GAME_WIDTH; x += 46) {
        if (Math.abs(x - safeCenter) > (rage ? 42 : 52)) {
          this.createEnemyBullet(x, -12 - ((x / 46) % 2) * 24, Math.PI / 2, speed * 1.08, damage, 0);
        }
      }
      if (volley % 2 === 0) {
        this.fireAimedBurst(boss.x, boss.y + 38, 3, 0.085, speed * 1.22, damage);
      }
    } else if (this.stageIndex === 5) {
      this.spiralAngle += rage ? 0.32 : 0.24;
      const count = rage ? 12 : 10;
      this.fireRadial(boss.x, boss.y + 12, count, speed * 0.72, damage, this.spiralAngle);
      this.fireRadial(boss.x, boss.y + 12, count, speed, damage, -this.spiralAngle * 0.72);
    } else if (this.stageIndex === 6) {
      const safeCenter = 82 + ((volley * 113) % (GAME_WIDTH - 164));
      for (let x = 24; x < GAME_WIDTH; x += 38) {
        if (Math.abs(x - safeCenter) > (rage ? 44 : 58)) {
          this.createEnemyBullet(
            x,
            -12 - ((x / 38) % 2) * 30,
            Math.PI / 2,
            speed * 1.24,
            damage,
            0,
          );
        }
      }
      if (volley % 3 === 0) {
        const aim = Math.atan2(this.player.y - boss.y, this.player.x - boss.x);
        this.fireFan(boss.x, boss.y + 38, aim, rage ? 7 : 5, 0.1, speed, damage);
      }
    } else {
      this.spiralAngle += rage ? 0.47 : 0.36;
      const streams = rage ? 7 : 5;
      for (let index = 0; index < streams; index += 1) {
        const angle = this.spiralAngle + (Math.PI * 2 * index) / streams;
        const turn = (index % 2 === 0 ? 1 : -1) * (rage ? 0.2 : 0.13);
        this.createEnemyBullet(boss.x, boss.y + 12, angle, speed * 0.9, damage, turn);
      }
      if (volley % 2 === 0) {
        this.fireRadial(boss.x, boss.y + 8, rage ? 14 : 10, speed * 0.72, damage, -this.spiralAngle);
      }
      if (volley % 3 === 0) {
        this.fireAimedBurst(boss.x, boss.y + 40, rage ? 5 : 3, 0.08, speed * 1.32, damage);
      }
    }
  }

  private fireAimed(x: number, y: number, speed: number, damage: number): void {
    const angle = Math.atan2(this.player.y - y, this.player.x - x);
    this.createEnemyBullet(x, y, angle, speed, damage, 0);
  }

  private fireAimedBurst(
    x: number,
    y: number,
    count: number,
    spread: number,
    speed: number,
    damage: number,
  ): void {
    const aim = Math.atan2(this.player.y - y, this.player.x - x);
    const center = (count - 1) / 2;
    for (let index = 0; index < count; index += 1) {
      this.createEnemyBullet(x, y, aim + (index - center) * spread, speed, damage, 0);
    }
  }

  private fireFan(
    x: number,
    y: number,
    centerAngle: number,
    count: number,
    spread: number,
    speed: number,
    damage: number,
  ): void {
    const center = (count - 1) / 2;
    for (let index = 0; index < count; index += 1) {
      this.createEnemyBullet(x, y, centerAngle + (index - center) * spread, speed, damage, 0);
    }
  }

  private fireRadial(
    x: number,
    y: number,
    count: number,
    speed: number,
    damage: number,
    offset = 0,
  ): void {
    for (let index = 0; index < count; index += 1) {
      const angle = offset + (Math.PI * 2 * index) / count;
      this.createEnemyBullet(x, y, angle, speed, damage, 0);
    }
  }

  private createEnemyBullet(
    x: number,
    y: number,
    angle: number,
    speed: number,
    damage: number,
    turnRate: number,
  ): void {
    const bulletLimit = this.compactViewport ? 360 : 520;
    if (this.bullets.length >= bulletLimit) return;
    this.bullets.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      radius: 4.1 + Math.min(1.7, this.stageIndex * 0.13),
      damage: damage * (0.85 + this.stageIndex * 0.032),
      friendly: false,
      dead: false,
      life: 7,
      color: this.stage.theme.danger,
      pierce: 1,
      turnRate,
      age: 0,
    });
  }

  private handleCollisions(): void {
    for (const bullet of this.bullets) {
      if (bullet.dead) continue;
      if (bullet.friendly) {
        for (const enemy of this.enemies) {
          if (enemy.dead || enemy.hp <= 0) continue;
          const collisionRadius = bullet.radius + enemy.radius * (enemy.kind === "boss" ? 0.82 : 0.72);
          if (distanceSquared(bullet.x, bullet.y, enemy.x, enemy.y) <= collisionRadius * collisionRadius) {
            enemy.hp -= bullet.damage;
            enemy.flash = 0.07;
            bullet.pierce -= 1;
            if (bullet.pierce <= 0) bullet.dead = true;
            this.spawnHitSpark(bullet.x, bullet.y, bullet.color);
            this.audio.hit();
            if (enemy.hp <= 0) this.destroyEnemy(enemy);
            if (bullet.dead) break;
          }
        }
      } else if (!this.player.dead && this.player.invincible <= 0) {
        const collisionRadius = bullet.radius + this.player.radius * 0.72;
        if (distanceSquared(bullet.x, bullet.y, this.player.x, this.player.y) <= collisionRadius * collisionRadius) {
          bullet.dead = true;
          this.damagePlayer(bullet.damage);
        }
      }
    }

    if (!this.player.dead) {
      for (const enemy of this.enemies) {
        if (enemy.dead || this.player.invincible > 0) continue;
        const bossContact = enemy.kind === "boss";
        const collisionRadius =
          enemy.radius * (bossContact ? 0.58 : 0.65) + this.player.radius * 0.75;
        if (distanceSquared(enemy.x, enemy.y, this.player.x, this.player.y) <= collisionRadius * collisionRadius) {
          if (bossContact) {
            this.player.y = clamp(
              enemy.y + enemy.radius + this.player.radius + 24,
              FRONTLINE_Y,
              GAME_HEIGHT - 30,
            );
            this.player.targetY = Math.max(this.player.targetY, this.player.y);
            this.damagePlayer(32 + this.stageIndex * 2.1);
          } else {
            enemy.hp = 0;
            this.destroyEnemy(enemy, false);
            this.damagePlayer(26 + this.stageIndex * 1.7);
          }
        }
      }

      for (const pickup of this.pickups) {
        if (pickup.dead) continue;
        const collisionRadius = pickup.radius + this.player.radius + 4;
        if (distanceSquared(pickup.x, pickup.y, this.player.x, this.player.y) <= collisionRadius * collisionRadius) {
          pickup.dead = true;
          this.collectPickup(pickup.kind);
          this.spawnBurst(pickup.x, pickup.y, pickupLabels[pickup.kind].color, 16, 145, false);
          this.rings.push({
            x: pickup.x,
            y: pickup.y,
            radius: 4,
            speed: 95,
            life: 0.42,
            maxLife: 0.42,
            color: pickupLabels[pickup.kind].color,
            width: 2,
          });
        }
      }
    }
  }

  private damagePlayer(amount: number): void {
    if (this.player.shield) {
      this.player.shield = false;
      this.player.invincible = 0.65;
      this.callbacks.onToast("相位护盾破裂", "DAMAGE ABSORBED");
      this.rings.push({
        x: this.player.x,
        y: this.player.y,
        radius: 18,
        speed: 120,
        life: 0.55,
        maxLife: 0.55,
        color: "#7f9fff",
        width: 3,
      });
      return;
    }

    this.player.hp = Math.max(0, this.player.hp - amount);
    this.player.invincible = 0.78;
    this.combo = 0;
    this.comboTimer = 0;
    this.screenShake = 10;
    this.lightningFlash = 0.2;
    this.spawnBurst(this.player.x, this.player.y, "#8a86ff", 22, 220, true);
    this.callbacks.onPlayerHit();
    this.audio.playerHit();
    this.emitHud(true);

    if (this.player.hp <= 0) {
      this.player.dead = true;
      this.completed = false;
      this.endingCountdown = 1.4;
      this.spawnBurst(this.player.x, this.player.y, "#55d9ff", 55, 350, true);
      this.rings.push({
        x: this.player.x,
        y: this.player.y,
        radius: 10,
        speed: 240,
        life: 0.7,
        maxLife: 0.7,
        color: "#55d9ff",
        width: 7,
      });
      this.audio.explode(2);
    }
  }

  private destroyEnemy(enemy: Enemy, reward = true): void {
    if (enemy.dead) return;
    enemy.dead = true;
    const boss = enemy.kind === "boss";
    const color = boss ? this.stage.theme.accent : this.stage.theme.danger;
    this.spawnBurst(enemy.x, enemy.y, color, boss ? 80 : 18, boss ? 410 : 220, true);
    this.rings.push({
      x: enemy.x,
      y: enemy.y,
      radius: boss ? 22 : 5,
      speed: boss ? 250 : 90,
      life: boss ? 1 : 0.4,
      maxLife: boss ? 1 : 0.4,
      color,
      width: boss ? 10 : 2,
    });
    this.screenShake = boss ? 22 : Math.max(this.screenShake, 2.5);
    this.audio.explode(boss ? 2 : enemy.kind === "tank" ? 1.3 : 0.7);

    if (reward) {
      this.combo += 1;
      this.maxCombo = Math.max(this.maxCombo, this.combo);
      this.comboTimer = 2.75;
      const multiplier = this.getMultiplier();
      const points = Math.round(enemy.score * multiplier);
      this.score += points;
      this.floatingTexts.push({
        x: enemy.x,
        y: enemy.y,
        text: `+${points}`,
        color: this.combo >= 16 ? "#b9f4ff" : "#eafcff",
        life: 0.9,
      });
    }

    if (boss) {
      this.boss = null;
      this.callbacks.onBoss(false, this.stage.boss, 0);
      this.completed = true;
      this.endingCountdown = 2.15;
      this.lightningFlash = 0.85;
      this.audio.stageClear();
      return;
    }

    this.enemyKills += 1;
    const guaranteed = this.enemyKills % GUARANTEED_PICKUP_INTERVAL === 0;
    const dropChance =
      BASE_PICKUP_DROP_CHANCE +
      (enemy.kind === "tank"
        ? TANK_PICKUP_DROP_BONUS
        : enemy.kind === "turret"
          ? TURRET_PICKUP_DROP_BONUS
          : 0);
    const activePickups = this.pickups.reduce(
      (count, pickup) => count + (pickup.dead ? 0 : 1),
      0,
    );
    if (activePickups < MAX_ACTIVE_PICKUPS && (guaranteed || chance(dropChance))) {
      this.spawnPickup(enemy.x, enemy.y, guaranteed ? this.guaranteedPickupKind() : this.randomPickupKind());
    }
  }

  private guaranteedPickupKind(): PickupKind {
    const sequence: PickupKind[] = [
      "power",
      "power",
      "wing",
      "power",
      "overdrive",
      "power",
      "shield",
      "power",
      "wing",
      "repair",
      "power",
      "bomb",
    ];
    return sequence[
      Math.floor(this.enemyKills / GUARANTEED_PICKUP_INTERVAL - 1) % sequence.length
    ];
  }

  private randomPickupKind(): PickupKind {
    const roll = Math.random();
    if (roll < 0.58) return "power";
    if (roll < 0.75) return "wing";
    if (roll < 0.84) return "shield";
    if (roll < 0.89) return "repair";
    if (roll < 0.98) return "overdrive";
    return "bomb";
  }

  private spawnPickup(x: number, y: number, kind: PickupKind): void {
    this.pickups.push({
      kind,
      x,
      y,
      vy: 78,
      age: Math.random() * Math.PI,
      radius: 14,
      dead: false,
    });
  }

  private collectPickup(kind: PickupKind): void {
    const label = pickupLabels[kind];
    let toastTitle = label.title;
    let toastCaption = label.caption;
    if (kind === "power") {
      if (this.player.weaponLevel < MAX_WEAPON_LEVEL) {
        this.player.weaponCharge += 1;
        const required = this.player.weaponLevel >= 6 ? 2 : 1;
        if (this.player.weaponCharge >= required) {
          this.player.weaponCharge = 0;
          this.player.weaponLevel += 1;
        }
      } else {
        this.score += 1000;
        toastTitle = "火力核心已超限";
        toastCaption = "MAX POWER / SCORE +1000";
      }
    } else if (kind === "wing") {
      if (this.player.wingLevel < MAX_WING_LEVEL) {
        this.player.wingLevel += 1;
      } else {
        this.score += 800;
      }
    } else if (kind === "shield") {
      this.player.shield = true;
    } else if (kind === "repair") {
      this.player.hp = Math.min(this.player.maxHp, this.player.hp + PLAYER_REPAIR_AMOUNT);
    } else if (kind === "overdrive") {
      this.player.overdrive = Math.max(this.player.overdrive, 8);
    } else {
      this.player.bombs = Math.min(5, this.player.bombs + 1);
    }
    this.audio.pickup(kind);
    this.callbacks.onToast(toastTitle, toastCaption);
    this.emitHud(true);
  }

  private cleanupEntities(): void {
    compactInPlace(this.bullets, liveBullet);
    compactInPlace(this.enemies, liveEnemy);
    compactInPlace(this.pickups, livePickup);
    compactInPlace(this.particles, liveParticle);
    compactInPlace(this.rings, liveRing);
    compactInPlace(this.floatingTexts, liveFloatingText);
  }

  private updateParticles(delta: number): void {
    this.particles.forEach((particle) => {
      particle.life -= delta;
      particle.x += particle.vx * delta;
      particle.y += particle.vy * delta;
      const drag = Math.pow(particle.drag, delta * 60);
      particle.vx *= drag;
      particle.vy *= drag;
    });
    this.rings.forEach((ring) => {
      ring.life -= delta;
      ring.radius += ring.speed * delta;
    });
    this.floatingTexts.forEach((text) => {
      text.life -= delta;
      text.y -= 26 * delta;
    });
  }

  private spawnHitSpark(x: number, y: number, color: string): void {
    const particleLimit = this.compactViewport ? 160 : 240;
    const count = Math.min(2, Math.max(0, particleLimit - this.particles.length));
    for (let index = 0; index < count; index += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 50 + Math.random() * 100;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 0.18 + Math.random() * 0.13,
        maxLife: 0.31,
        size: 1 + Math.random() * 1.5,
        color,
        drag: 0.92,
        line: true,
      });
    }
  }

  private spawnBurst(
    x: number,
    y: number,
    color: string,
    count: number,
    maxSpeed: number,
    lines: boolean,
  ): void {
    const particleLimit = this.compactViewport ? 160 : 240;
    const allowedCount = Math.min(count, Math.max(0, particleLimit - this.particles.length));
    for (let index = 0; index < allowedCount; index += 1) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 35 + Math.random() * maxSpeed;
      const life = 0.28 + Math.random() * 0.65;
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life,
        maxLife: life,
        size: 1 + Math.random() * (allowedCount > 30 ? 4.5 : 2.8),
        color: chance(0.28) ? "#f7e3ad" : color,
        drag: 0.955,
        line: lines && chance(0.63),
      });
    }
  }

  private emitHud(force = false): void {
    if (!force && this.phase !== "playing") return;
    if (this.boss && !this.boss.dead) {
      this.callbacks.onBoss(
        true,
        this.stage.boss,
        clamp(this.boss.hp / this.boss.maxHp, 0, 1),
      );
    }
    this.callbacks.onHud({
      stageIndex: this.stageIndex,
      stageName: this.stage.name,
      hp: this.player.hp,
      maxHp: this.player.maxHp,
      weaponLevel: this.player.weaponLevel,
      weaponCharge: this.player.weaponCharge,
      wingLevel: this.player.wingLevel,
      bombs: this.player.bombs,
      score: this.score,
      combo: this.combo,
      multiplier: this.getMultiplier(),
      progress: this.bossSpawned ? 1 : clamp(this.stageElapsed / this.stage.duration, 0, 1),
    });
  }

  private getMultiplier(): number {
    return 1 + Math.min(3, Math.floor(this.combo / 8) * 0.25);
  }

  private finalizeStage(): void {
    this.phase = "ended";
    this.audio.stopMusic();
    const result = this.createResult();
    if (this.completed) {
      this.callbacks.onComplete(result);
    } else {
      this.callbacks.onGameOver(result);
    }
  }

  private createResult(): GameResult {
    const hpPercent = Math.round((this.player.hp / this.player.maxHp) * 100);
    const performanceScore = hpPercent + Math.min(75, this.maxCombo * 1.4) + Math.min(45, this.score / 2500);
    const rank: GameResult["rank"] =
      performanceScore >= 155 ? "S" : performanceScore >= 115 ? "A" : performanceScore >= 72 ? "B" : "C";
    return {
      stageIndex: this.stageIndex,
      score: Math.round(this.score),
      maxCombo: this.maxCombo,
      hpPercent,
      rank,
    };
  }

  private draw(): void {
    const context = this.context;
    context.save();
    this.drawBackground();
    this.drawFrontlinePressure();
    this.drawStageHazard();

    if (this.screenShake > 0) {
      context.translate(
        (Math.random() - 0.5) * this.screenShake,
        (Math.random() - 0.5) * this.screenShake,
      );
    }

    this.drawPickups();
    this.drawEnemies();
    this.drawBullets();
    this.drawPlayer();
    this.drawParticles();
    context.restore();

    if (this.lightningFlash > 0) {
      context.fillStyle = `rgba(210,235,255,${this.lightningFlash * 0.48})`;
      context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    }
  }

  private drawBackground(): void {
    const context = this.context;
    const theme = this.stage.theme;
    context.fillStyle = this.backgroundGradient ?? theme.skyTop;
    context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    context.fillStyle = this.backgroundHaze ?? `${theme.haze}18`;
    context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    context.save();
    for (const star of this.stars) {
      context.globalAlpha = star.alpha;
      context.fillStyle = star.speed > 95 ? theme.accent : "#e8f4f5";
      context.fillRect(star.x, star.y, star.size, star.size * (this.phase === "playing" ? 2.4 : 1));
    }
    context.restore();

    this.drawHorizonGrid();
    this.drawWeather();
    this.drawStageMotif();

    context.fillStyle = this.backgroundVignette ?? "rgba(0,0,0,0.32)";
    context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
  }

  private drawFrontlinePressure(): void {
    if (this.phase !== "playing" || this.player.dead) return;
    const context = this.context;
    const active = this.player.y < FRONTLINE_Y || this.frontlineExposure > 0;
    const pulse = 0.62 + Math.sin(this.worldTime * 12) * 0.2;

    context.save();
    context.fillStyle = this.stage.theme.danger;
    context.globalAlpha = active ? 0.08 + this.frontlineExposure * 0.05 : 0.025;
    context.fillRect(0, 0, GAME_WIDTH, FRONTLINE_Y);

    context.globalAlpha = active ? pulse : 0.12;
    context.strokeStyle = this.stage.theme.danger;
    context.lineWidth = active ? 1.7 : 0.8;
    context.setLineDash([9, 9]);
    context.beginPath();
    context.moveTo(0, FRONTLINE_Y);
    context.lineTo(GAME_WIDTH, FRONTLINE_Y);
    context.stroke();
    context.setLineDash([]);

    context.fillStyle = this.stage.theme.danger;
    context.font = '8px "Bodoni 72 Smallcaps", serif';
    context.textAlign = "left";
    context.fillText("FRONTLINE // HULL OVERLOAD", 13, FRONTLINE_Y - 10);

    if (active) {
      for (let x = 44; x < GAME_WIDTH; x += 78) {
        context.beginPath();
        context.moveTo(x - 7, FRONTLINE_Y - 25);
        context.lineTo(x, FRONTLINE_Y - 15);
        context.lineTo(x + 7, FRONTLINE_Y - 25);
        context.stroke();
      }
    }
    context.restore();
  }

  private drawStageHazard(): void {
    if (this.hazard.warning <= 0 && this.hazard.active <= 0) return;
    const context = this.context;
    const kind = this.stage.hazardKind;
    const warning = this.hazard.warning > 0;
    const pulse = 0.55 + Math.sin(this.worldTime * 15) * 0.2;
    const intensity = warning ? pulse : 0.7;

    context.save();
    context.lineWidth = warning ? 2 : 2.8;
    context.strokeStyle = this.stage.theme.accent;
    context.fillStyle = this.stage.theme.accent;
    context.globalAlpha = intensity;
    context.setLineDash(warning ? [9, 8] : []);

    if (kind === "rain") {
      context.globalAlpha = warning ? 0.18 : 0.1;
      context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
      context.globalAlpha = intensity;
      for (let y = 150; y < GAME_HEIGHT; y += 112) {
        const startX = this.hazard.direction > 0 ? 18 : GAME_WIDTH - 18;
        const endX = startX + this.hazard.direction * 66;
        context.beginPath();
        context.moveTo(startX, y);
        context.lineTo(endX, y);
        context.lineTo(endX - this.hazard.direction * 12, y - 8);
        context.moveTo(endX, y);
        context.lineTo(endX - this.hazard.direction * 12, y + 8);
        context.stroke();
      }
    } else if (kind === "embers") {
      const halfWidth = 43;
      context.globalAlpha = warning ? 0.12 : 0.24;
      context.fillRect(this.hazard.x - halfWidth, 0, halfWidth * 2, GAME_HEIGHT);
      context.globalAlpha = intensity;
      context.strokeRect(this.hazard.x - halfWidth, 0, halfWidth * 2, GAME_HEIGHT);
      for (let y = 48; y < GAME_HEIGHT; y += 84) {
        context.beginPath();
        context.moveTo(this.hazard.x, y + 26);
        context.lineTo(this.hazard.x - 10, y + 8);
        context.moveTo(this.hazard.x, y + 26);
        context.lineTo(this.hazard.x + 10, y + 8);
        context.stroke();
      }
    } else if (kind === "sand") {
      context.globalAlpha = warning ? 0.15 : 0.09;
      context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
      context.globalAlpha = intensity;
      for (let index = 0; index < 5; index += 1) {
        const x = 42 + index * 99;
        const y = 44 + Math.abs(index - 2) * 34;
        context.beginPath();
        context.moveTo(x, y + 20);
        context.lineTo(x - 12, y - 4);
        context.lineTo(x + 12, y - 4);
        context.closePath();
        context.stroke();
      }
    } else if (kind === "aurora") {
      context.globalAlpha = warning ? 0.28 : 0.4;
      for (let band = 0; band < 4; band += 1) {
        const y = 180 + band * 145;
        context.beginPath();
        context.moveTo(0, y);
        context.bezierCurveTo(
          120,
          y + this.hazard.direction * 42,
          360,
          y - this.hazard.direction * 42,
          GAME_WIDTH,
          y,
        );
        context.stroke();
      }
    } else if (kind === "ice") {
      context.globalAlpha = warning ? 0.08 : 0.15;
      context.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
      context.globalAlpha = intensity;
      const radius = warning ? 48 + (1 - this.hazard.warning / HAZARD_RULES.ice.warning) * 88 : 94;
      context.beginPath();
      context.arc(this.player.x, this.player.y, radius, 0, Math.PI * 2);
      context.stroke();
      context.strokeRect(9, 9, GAME_WIDTH - 18, GAME_HEIGHT - 18);
    } else if (kind === "void" || kind === "singularity") {
      context.globalAlpha = warning ? 0.55 : 0.72;
      const baseRadius = warning ? 24 : 38;
      for (let index = 0; index < 5; index += 1) {
        context.beginPath();
        context.arc(
          this.hazard.x,
          this.hazard.y,
          baseRadius + index * 22 + Math.sin(this.worldTime * 4 + index) * 4,
          0,
          Math.PI * 2,
        );
        context.stroke();
      }
      if (kind === "singularity") {
        context.globalAlpha = 0.18;
        context.fillStyle = this.stage.theme.accent;
        context.beginPath();
        context.arc(this.hazard.x, this.hazard.y, 44, 0, Math.PI * 2);
        context.fill();
      }
    } else {
      const lanes = [this.hazard.x, this.hazard.secondaryX];
      lanes.forEach((x) => {
        context.globalAlpha = warning ? 0.13 : 0.36;
        context.fillRect(x - 32, 0, 64, GAME_HEIGHT);
        context.globalAlpha = intensity;
        context.strokeRect(x - 32, 0, 64, GAME_HEIGHT);
        if (!warning) {
          context.beginPath();
          context.moveTo(x, 0);
          for (let y = 0; y <= GAME_HEIGHT; y += 62) {
            context.lineTo(x + (y % 124 === 0 ? 13 : -13), y);
          }
          context.stroke();
        }
      });
    }

    context.restore();
  }

  private drawHorizonGrid(): void {
    const context = this.context;
    const theme = this.stage.theme;
    const horizon = 350;
    context.save();
    context.strokeStyle = `${theme.accent}16`;
    context.lineWidth = 0.7;
    for (let index = 0; index < 13; index += 1) {
      const t = ((index / 13 + (this.worldTime * 0.06) % (1 / 13)) % 1);
      const y = horizon + Math.pow(t, 2.2) * (GAME_HEIGHT - horizon);
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(GAME_WIDTH, y);
      context.stroke();
    }
    for (let index = -8; index <= 8; index += 1) {
      context.beginPath();
      context.moveTo(GAME_WIDTH / 2 + index * 12, horizon);
      context.lineTo(GAME_WIDTH / 2 + index * 95, GAME_HEIGHT);
      context.stroke();
    }
    context.restore();
  }

  private drawWeather(): void {
    const context = this.context;
    const kind = this.stage.hazardKind;
    context.save();

    if (kind === "rain") {
      context.strokeStyle = "rgba(150,235,245,0.25)";
      context.lineWidth = 1;
      this.weather.forEach((drop) => {
        context.beginPath();
        context.moveTo(drop.x, drop.y);
        context.lineTo(drop.x - 8, drop.y - 29 - drop.size * 5);
        context.stroke();
      });
    } else if (kind === "embers") {
      this.weather.forEach((ember) => {
        context.globalAlpha = ember.alpha;
        context.fillStyle = chance(0.03) ? "#eafcff" : this.stage.theme.accent;
        context.fillRect(ember.x, ember.y, ember.size, ember.size * 2.8);
      });
    } else if (kind === "sand") {
      context.fillStyle = "rgba(138,134,255,0.14)";
      this.weather.forEach((grain) => {
        context.globalAlpha = grain.alpha;
        context.fillRect(grain.x, grain.y, grain.size * 1.6, Math.max(0.6, grain.size * 0.45));
      });
    } else if (kind === "aurora") {
      for (let band = 0; band < 3; band += 1) {
        const x = 70 + band * 155;
        context.globalAlpha = 0.035 + band * 0.01;
        context.fillStyle = this.stage.theme.accent;
        context.beginPath();
        context.moveTo(x + Math.sin(this.worldTime + band) * 30, -20);
        context.bezierCurveTo(x + 120, 230, x - 80, 510, x + 70, 830);
        context.lineTo(x + 115, 830);
        context.bezierCurveTo(x - 20, 510, x + 165, 230, x + 45, -20);
        context.closePath();
        context.fill();
      }
    } else if (kind === "ice") {
      context.strokeStyle = "rgba(205,243,255,0.22)";
      this.weather.forEach((shard) => {
        context.globalAlpha = shard.alpha;
        context.save();
        context.translate(shard.x, shard.y);
        context.rotate(shard.phase + this.worldTime * 0.4);
        context.beginPath();
        context.moveTo(0, -shard.size * 3);
        context.lineTo(shard.size, 0);
        context.lineTo(0, shard.size * 3);
        context.lineTo(-shard.size, 0);
        context.closePath();
        context.stroke();
        context.restore();
      });
    } else if (kind === "void" || kind === "singularity") {
      const cx = kind === "void" ? 120 : GAME_WIDTH / 2;
      const cy = kind === "void" ? 235 : 190;
      for (let ring = 0; ring < 5; ring += 1) {
        context.strokeStyle = `${this.stage.theme.accent}${10 + ring * 4}`;
        context.lineWidth = 0.7;
        context.beginPath();
        context.ellipse(
          cx,
          cy,
          42 + ring * 29 + Math.sin(this.worldTime + ring) * 5,
          17 + ring * 10,
          this.worldTime * 0.08,
          0,
          Math.PI * 2,
        );
        context.stroke();
      }
    } else {
      context.strokeStyle = "rgba(116,181,255,0.2)";
      context.lineWidth = 1;
      this.weather.slice(0, 24).forEach((spark) => {
        context.beginPath();
        context.moveTo(spark.x, spark.y);
        context.lineTo(spark.x + Math.sin(spark.phase) * 7, spark.y + 18);
        context.lineTo(spark.x - Math.cos(spark.phase) * 4, spark.y + 32);
        context.stroke();
      });
    }
    context.restore();
  }

  private drawStageMotif(): void {
    const context = this.context;
    context.save();
    context.translate(GAME_WIDTH / 2, 230);
    context.rotate(this.worldTime * (this.stage.hazardKind === "singularity" ? 0.045 : 0.012));
    context.strokeStyle = `${this.stage.theme.accent}12`;
    context.lineWidth = 1;
    const sides = 6 + (this.stageIndex % 3);
    for (let ring = 0; ring < 3; ring += 1) {
      const radius = 110 + ring * 55;
      context.beginPath();
      for (let index = 0; index <= sides; index += 1) {
        const angle = (Math.PI * 2 * index) / sides;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        if (index === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      }
      context.stroke();
    }
    context.restore();
  }

  private drawBullets(): void {
    const context = this.context;
    context.save();
    context.globalCompositeOperation = "lighter";
    this.bullets.forEach((bullet) => {
      context.save();
      context.translate(bullet.x, bullet.y);
      const angle = Math.atan2(bullet.vy, bullet.vx);
      context.rotate(angle + Math.PI / 2);
      context.fillStyle = bullet.color;
      if (bullet.friendly) {
        context.globalAlpha = 0.28;
        context.fillRect(-bullet.radius * 1.8, -15, bullet.radius * 3.6, 25);
        context.globalAlpha = 1;
        context.fillRect(-bullet.radius * 0.55, -12, bullet.radius * 1.1, 18);
      } else {
        context.globalAlpha = 0.25;
        context.beginPath();
        context.arc(0, 0, bullet.radius * 2.1, 0, Math.PI * 2);
        context.fill();
        context.globalAlpha = 1;
        context.beginPath();
        context.arc(0, 0, bullet.radius, 0, Math.PI * 2);
        context.fill();
        context.fillStyle = "#eafcff";
        context.beginPath();
        context.arc(-bullet.radius * 0.2, -bullet.radius * 0.2, bullet.radius * 0.32, 0, Math.PI * 2);
        context.fill();
      }
      context.restore();
    });
    context.restore();
  }

  private drawEnemies(): void {
    this.enemies.forEach((enemy) => {
      if (enemy.kind === "boss") {
        this.drawBoss(enemy);
      } else {
        this.drawEnemy(enemy);
      }
    });
  }

  private drawEnemy(enemy: Enemy): void {
    const context = this.context;
    context.save();
    context.translate(enemy.x, enemy.y);
    const angle = enemy.kind === "charger" ? Math.atan2(enemy.vy, enemy.vx) + Math.PI / 2 : 0;
    context.rotate(angle);
    context.globalAlpha = enemy.flash > 0 ? 1 : 0.96;
    context.shadowBlur = enemy.flash > 0 ? 18 : 7;
    context.shadowColor = enemy.flash > 0 ? "#ffffff" : this.stage.theme.danger;
    context.fillStyle = enemy.flash > 0 ? "#ffffff" : "#0b1430";
    context.strokeStyle = this.stage.theme.danger;
    context.lineWidth = 1.2;

    if (enemy.kind === "scout") {
      context.beginPath();
      context.moveTo(0, 17);
      context.lineTo(-22, -7);
      context.lineTo(-6, -3);
      context.lineTo(0, -18);
      context.lineTo(6, -3);
      context.lineTo(22, -7);
      context.closePath();
      context.fill();
      context.stroke();
    } else if (enemy.kind === "weaver") {
      context.rotate(Math.sin(enemy.age * 2) * 0.18);
      context.beginPath();
      context.moveTo(0, 21);
      context.lineTo(-24, 3);
      context.lineTo(-13, -13);
      context.lineTo(0, -6);
      context.lineTo(13, -13);
      context.lineTo(24, 3);
      context.closePath();
      context.fill();
      context.stroke();
    } else if (enemy.kind === "turret") {
      context.rotate(enemy.age * 0.28);
      context.beginPath();
      for (let index = 0; index < 8; index += 1) {
        const radius = index % 2 === 0 ? 22 : 16;
        const pointAngle = (Math.PI * 2 * index) / 8;
        const x = Math.cos(pointAngle) * radius;
        const y = Math.sin(pointAngle) * radius;
        if (index === 0) context.moveTo(x, y);
        else context.lineTo(x, y);
      }
      context.closePath();
      context.fill();
      context.stroke();
      context.rotate(-enemy.age * 0.6);
      context.strokeRect(-7, -7, 14, 14);
    } else if (enemy.kind === "charger") {
      context.beginPath();
      context.moveTo(0, 24);
      context.lineTo(-15, -17);
      context.lineTo(0, -7);
      context.lineTo(15, -17);
      context.closePath();
      context.fill();
      context.stroke();
      context.fillStyle = this.stage.theme.danger;
      context.fillRect(-3, -19, 6, 10);
    } else {
      context.beginPath();
      context.moveTo(0, 28);
      context.lineTo(-30, 9);
      context.lineTo(-24, -17);
      context.lineTo(-8, -22);
      context.lineTo(0, -15);
      context.lineTo(8, -22);
      context.lineTo(24, -17);
      context.lineTo(30, 9);
      context.closePath();
      context.fill();
      context.stroke();
      context.fillStyle = this.stage.theme.danger;
      context.fillRect(-4, -6, 8, 20);
    }

    context.fillStyle = this.stage.theme.danger;
    context.globalAlpha = 0.85;
    context.beginPath();
    context.arc(0, enemy.kind === "tank" ? 1 : 2, enemy.kind === "tank" ? 6 : 4, 0, Math.PI * 2);
    context.fill();

    if (enemy.kind === "tank" || enemy.kind === "turret") {
      const ratio = clamp(enemy.hp / enemy.maxHp, 0, 1);
      context.globalAlpha = 0.75;
      context.fillStyle = "rgba(0,0,0,0.65)";
      context.fillRect(-enemy.radius, enemy.radius + 9, enemy.radius * 2, 3);
      context.fillStyle = this.stage.theme.danger;
      context.fillRect(-enemy.radius, enemy.radius + 9, enemy.radius * 2 * ratio, 3);
    }
    context.restore();
  }

  private drawBoss(boss: Enemy): void {
    const context = this.context;
    const pulse = 1 + Math.sin(this.worldTime * 3.2) * 0.025;
    context.save();
    context.translate(boss.x, boss.y);
    context.scale(pulse, pulse);
    context.shadowBlur = boss.flash > 0 ? 24 : 15;
    context.shadowColor = boss.flash > 0 ? this.stage.theme.danger : this.stage.theme.accent;

    context.fillStyle = "#071126";
    context.strokeStyle = boss.flash > 0 ? "#eafcff" : this.stage.theme.accent;
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(0, 62);
    context.lineTo(-32, 41);
    context.lineTo(-78, 29);
    context.lineTo(-96, -5);
    context.lineTo(-55, 5);
    context.lineTo(-69, -42);
    context.lineTo(-25, -32);
    context.lineTo(0, -66);
    context.lineTo(25, -32);
    context.lineTo(69, -42);
    context.lineTo(55, 5);
    context.lineTo(96, -5);
    context.lineTo(78, 29);
    context.lineTo(32, 41);
    context.closePath();
    context.fill();
    context.stroke();

    context.globalAlpha = 0.6;
    context.strokeStyle = this.stage.theme.danger;
    context.lineWidth = 1;
    context.beginPath();
    context.moveTo(-69, -42);
    context.lineTo(-25, 10);
    context.lineTo(0, 62);
    context.lineTo(25, 10);
    context.lineTo(69, -42);
    context.stroke();

    context.globalAlpha = 1;
    const core = context.createRadialGradient(0, 0, 0, 0, 0, 27);
    core.addColorStop(0, "#eafcff");
    core.addColorStop(0.2, this.stage.theme.accent);
    core.addColorStop(1, `${this.stage.theme.accent}00`);
    context.fillStyle = core;
    context.beginPath();
    context.arc(0, 0, 28, 0, Math.PI * 2);
    context.fill();
    context.strokeStyle = this.stage.theme.accent;
    context.rotate(-this.worldTime * 0.65);
    context.strokeRect(-18, -18, 36, 36);
    context.rotate(this.worldTime * 1.3);
    context.strokeRect(-12, -12, 24, 24);

    context.fillStyle = this.stage.theme.danger;
    context.shadowColor = this.stage.theme.danger;
    context.shadowBlur = 18;
    [-66, 66].forEach((x) => {
      context.beginPath();
      context.arc(x, 15, 7, 0, Math.PI * 2);
      context.fill();
    });
    context.restore();
  }

  private drawPlayer(): void {
    if (this.player.dead || this.phase === "idle") return;
    const context = this.context;
    const flicker = this.player.invincible > 0 && Math.floor(this.player.invincible * 16) % 2 === 0;
    if (flicker) return;
    const playerScale = this.compactViewport ? 0.72 : 1;

    context.save();
    context.translate(this.player.x, this.player.y);
    context.scale(playerScale, playerScale);
    context.globalCompositeOperation = "lighter";
    const thrusterLength = 24 + Math.sin(this.worldTime * 24) * 5 + (this.player.overdrive > 0 ? 14 : 0);
    const thruster = context.createLinearGradient(0, 10, 0, thrusterLength + 12);
    thruster.addColorStop(0, "#eafcff");
    thruster.addColorStop(0.35, this.player.overdrive > 0 ? "#b9f4ff" : this.stage.theme.accent);
    thruster.addColorStop(1, "rgba(60,210,255,0)");
    context.fillStyle = thruster;
    context.beginPath();
    context.moveTo(-6, 12);
    context.lineTo(0, thrusterLength + 16);
    context.lineTo(6, 12);
    context.closePath();
    context.fill();
    context.restore();

    context.save();
    context.translate(this.player.x, this.player.y);
    context.scale(playerScale, playerScale);
    context.shadowBlur = this.player.overdrive > 0 ? 20 : 11;
    context.shadowColor = this.player.overdrive > 0 ? "#b9f4ff" : this.stage.theme.accent;
    context.fillStyle = "#ddf7ff";
    context.strokeStyle = this.player.overdrive > 0 ? "#b9f4ff" : this.stage.theme.accent;
    context.lineWidth = 1.4;
    context.beginPath();
    context.moveTo(0, -28);
    context.lineTo(-9, -4);
    context.lineTo(-31, 14);
    context.lineTo(-16, 17);
    context.lineTo(-7, 10);
    context.lineTo(-5, 24);
    context.lineTo(0, 18);
    context.lineTo(5, 24);
    context.lineTo(7, 10);
    context.lineTo(16, 17);
    context.lineTo(31, 14);
    context.lineTo(9, -4);
    context.closePath();
    context.fill();
    context.stroke();

    context.fillStyle = "#16212a";
    context.beginPath();
    context.moveTo(0, -19);
    context.lineTo(-5, 5);
    context.lineTo(0, 11);
    context.lineTo(5, 5);
    context.closePath();
    context.fill();

    context.fillStyle = this.player.overdrive > 0 ? "#b9f4ff" : this.stage.theme.accent;
    context.beginPath();
    context.arc(0, 1, 3.8, 0, Math.PI * 2);
    context.fill();

    for (let wing = 0; wing < this.player.wingLevel; wing += 1) {
      const offset = 27 + wing * 8;
      const bob = Math.sin(this.worldTime * 4 + wing) * 2;
      context.fillStyle = "#18242b";
      context.strokeStyle = this.stage.theme.accent;
      context.fillRect(-offset - 5, 4 + bob, 10, 17);
      context.strokeRect(-offset - 5, 4 + bob, 10, 17);
      context.fillRect(offset - 5, 4 + bob, 10, 17);
      context.strokeRect(offset - 5, 4 + bob, 10, 17);
    }

    if (this.player.shield) {
      context.strokeStyle = "#7f9fff";
      context.lineWidth = 2;
      context.globalAlpha = 0.55 + Math.sin(this.worldTime * 5) * 0.16;
      context.beginPath();
      context.ellipse(0, 0, 40, 46, 0, 0, Math.PI * 2);
      context.stroke();
      context.globalAlpha = 0.18;
      context.fillStyle = "#7f9fff";
      context.fill();
    }
    context.restore();
  }

  private drawPickups(): void {
    const context = this.context;
    this.pickups.forEach((pickup) => {
      const label = pickupLabels[pickup.kind];
      const pulse = 1 + Math.sin(pickup.age * 6) * 0.08;
      context.save();
      context.translate(pickup.x, pickup.y);
      context.rotate(pickup.age * 1.7);
      context.scale(pulse, pulse);
      context.shadowBlur = 12;
      context.shadowColor = label.color;
      context.fillStyle = `${label.color}25`;
      context.strokeStyle = label.color;
      context.lineWidth = 1.4;
      context.beginPath();
      context.rect(-13, -13, 26, 26);
      context.fill();
      context.stroke();
      context.rotate(-pickup.age * 3.4);
      context.strokeRect(-9, -9, 18, 18);
      context.rotate(pickup.age * 1.7);
      context.fillStyle = "#f7f2e8";
      context.font = 'bold 11px "STKaiti", serif';
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(label.glyph, 0, 1);
      context.restore();
    });
  }

  private drawParticles(): void {
    const context = this.context;
    context.save();
    context.globalCompositeOperation = "lighter";
    this.particles.forEach((particle) => {
      const alpha = clamp(particle.life / particle.maxLife, 0, 1);
      context.globalAlpha = alpha;
      context.fillStyle = particle.color;
      context.strokeStyle = particle.color;
      context.lineWidth = particle.size;
      if (particle.line) {
        context.beginPath();
        context.moveTo(particle.x, particle.y);
        context.lineTo(particle.x - particle.vx * 0.035, particle.y - particle.vy * 0.035);
        context.stroke();
      } else {
        context.beginPath();
        context.arc(particle.x, particle.y, particle.size * alpha, 0, Math.PI * 2);
        context.fill();
      }
    });
    this.rings.forEach((ring) => {
      const alpha = clamp(ring.life / ring.maxLife, 0, 1);
      context.globalAlpha = alpha;
      context.strokeStyle = ring.color;
      context.lineWidth = ring.width * alpha;
      context.beginPath();
      context.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2);
      context.stroke();
    });
    context.globalCompositeOperation = "source-over";
    context.textAlign = "center";
    context.font = '11px "Bodoni 72 Smallcaps", serif';
    this.floatingTexts.forEach((floatingText) => {
      context.globalAlpha = clamp(floatingText.life / 0.9, 0, 1);
      context.fillStyle = floatingText.color;
      context.fillText(floatingText.text, floatingText.x, floatingText.y);
    });
    context.restore();
  }
}
